import { TIMEOUT_FETCH_DEFAULT, TIMEOUT_FETCH_MAX, TIMEOUT_FETCH_MIN } from '../constants';
import { validateNumber } from '.';

interface FetchResponseBase {
    status: number;
    ok: boolean;
    headers: Record<string, string>;
    raw: () => string | null;
    error?: string;
}

interface FetchResponseJson<T = unknown> extends FetchResponseBase {
    isJson: true;
    data: T;
}

interface FetchResponseNonJson extends FetchResponseBase {
    isJson: false;
    data: null;
}

export type FetchResponse<T = unknown> = FetchResponseJson<T> | FetchResponseNonJson;

export async function safeFetch<T = unknown>(url: string, options: RequestInit = {}, timeout = 10000): Promise<FetchResponse<T>> {
    const timeoutMs = validateNumber(timeout, TIMEOUT_FETCH_MIN, TIMEOUT_FETCH_MAX, TIMEOUT_FETCH_DEFAULT);
    let controller: AbortController | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // Prefer modern AbortSignal.timeout, fallback to AbortController
    if (AbortSignal !== undefined && typeof AbortSignal.timeout === 'function') {
        options.signal = (AbortSignal as any).timeout(timeoutMs);
    } else if (AbortController !== undefined) {
        controller = new AbortController();
        timer = setTimeout(() => controller!.abort(), timeoutMs);
        options.signal = controller.signal;
    }

    try {
        const response = await fetch(url, options);
        if (timer) {
            clearTimeout(timer);
        }

        const headers = headersToObject(response.headers);

        if (!response.ok) {
            // Optional: můžeš číst body i u errorů, nebo jej přeskočit
            let rawText: string | null = null;
            try {
                rawText = await response.text();
            } catch {}
            return {
                status: response.status,
                ok: false,
                isJson: false,
                headers,
                data: null,
                raw: () => rawText,
                error: `HTTP error: ${response.status} ${response.statusText}`.trim(),
            };
        }

        const rawText = await response.text();
        try {
            const data = JSON.parse(rawText);
            return {
                status: response.status,
                ok: true,
                isJson: true,
                headers,
                data,
                raw: () => rawText,
            };
        } catch {
            return {
                status: response.status,
                ok: true,
                isJson: false,
                headers,
                data: null,
                raw: () => rawText,
                error: 'Response is not valid JSON.',
            };
        }
    } catch (error_: any) {
        if (timer) clearTimeout(timer);
        return {
            status: 0,
            ok: false,
            isJson: false,
            headers: {},
            data: null,
            raw: () => null,
            error: error_?.name === 'AbortError' ? 'Request timed out.' : `Unexpected error: ${error_?.message || String(error_)}`,
        };
    }
}

function headersToObject(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
        result[key] = value;
    });
    return result;
}
